import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { createAuthenticatedWebSocket } from '../services/api';
import toast from 'react-hot-toast';
import { getTerminology, normalizeEntityCategory } from '../utils/terminology';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      authAPI.getMe()
        .then((res) => {
          const freshUser = { ...parsedUser, ...res.data };
          localStorage.setItem('user', JSON.stringify(freshUser));
          setUser(freshUser);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user || !localStorage.getItem('token')) return undefined;
    let socket;
    try {
      socket = createAuthenticatedWebSocket();
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'NOTIFICATION' && message.data?.message) {
            toast(message.data.message, { duration: 10000, icon: '⏰' });
          }
        } catch {
          // Ignore malformed WebSocket messages.
        }
      };
    } catch {
      return undefined;
    }
    return () => socket?.close();
  }, [user]);

  const login = async (entityCode, email, password) => {
    const res = await authAPI.login({ entity_code: entityCode, email, password });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    localStorage.setItem('entity_code', entityCode);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('entity_code');
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(permission);
  };

  const entityCategory = normalizeEntityCategory(user?.entity?.category);
  const terminology = getTerminology(entityCategory);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, hasPermission, entityCategory, terminology }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
